import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, getRootOperator, OperatorType } from '@fabric-ui/components';
import { shouldExpandFilterGroup } from '../expressionOperations';

const stableStringify = (value: unknown): string =>
  JSON.stringify(value, (_, v) =>
    v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => a.localeCompare(b))) : v
  );

const getFilterShape = (filter: Filter): string => {
  const operator = getRootOperator<OperatorType.AND | OperatorType.OR>(filter);
  if (operator === OperatorType.AND || operator === OperatorType.OR) {
    return JSON.stringify([operator, (filter[operator]?.operands ?? []).map(getFilterShape)]);
  }
  if (filter.expression) {
    const conditionTypes = Object.entries(filter.expression)
      .filter(([key]) => key !== 'fieldName')
      .flatMap(([key, condition]) => [key, stableStringify(condition ?? {})]);
    return JSON.stringify(['expression', filter.expression.fieldName ?? null, conditionTypes]);
  }
  return JSON.stringify(Object.keys(filter).sort());
};

const collectRequiredExpandedGroupPaths = (filter: Filter, hasListTab: boolean, path: string, paths: Set<string>) => {
  const operator = getRootOperator<OperatorType.AND | OperatorType.OR>(filter);
  if (operator !== OperatorType.AND && operator !== OperatorType.OR) return;

  (filter[operator]?.operands ?? []).forEach((operand, index) => {
    const operandOperator = getRootOperator<OperatorType.AND | OperatorType.OR>(operand);
    if (operandOperator !== OperatorType.AND && operandOperator !== OperatorType.OR) return;

    const operandPath = path ? `${path}.${index}` : `${index}`;
    if (shouldExpandFilterGroup(operand, hasListTab)) paths.add(operandPath);
    collectRequiredExpandedGroupPaths(operand, hasListTab, operandPath, paths);
  });
};

const getFilterAtPath = (filter: Filter, path: string): Filter | undefined =>
  path.split('.').reduce<Filter | undefined>((current, segment) => {
    if (!current) return undefined;
    const operator = getRootOperator<OperatorType.AND | OperatorType.OR>(current);
    if (operator !== OperatorType.AND && operator !== OperatorType.OR) return undefined;
    return current[operator]?.operands?.[Number(segment)];
  }, filter);

const isExplicitPathValid = (filter: Filter, path: string, anchorShape: string): boolean => {
  const group = getFilterAtPath(filter, path);
  if (!group) return false;
  const operator = getRootOperator<OperatorType.AND | OperatorType.OR>(group);
  if (operator !== OperatorType.AND && operator !== OperatorType.OR) return false;
  return (group[operator]?.operands ?? []).some(operand => getFilterShape(operand) === anchorShape);
};

interface ExplicitGroup {
  anchorShape: string;
  sourceShape: string;
}

type ExplicitGroups = Map<string, ExplicitGroup>;

// Combine groups required by the filter tree with locally expanded groups anchored to their original operands.
// Keep pending expansions until the tree updates, discard stale anchors, and reuse the paths Set while its contents are unchanged.
export const useExpandedGroupPaths = (filter: Filter, hasListTab: boolean) => {
  const [explicitGroups, setExplicitGroups] = useState<ExplicitGroups>(() => new Map());
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const expandGroup = useCallback(
    (path: string, anchor: Filter) =>
      setExplicitGroups(groups =>
        new Map(groups).set(path, { anchorShape: getFilterShape(anchor), sourceShape: getFilterShape(filterRef.current) })
      ),
    []
  );
  const evaluatedExplicitGroups = useMemo(
    () =>
      [...explicitGroups].flatMap(([path, group]) => {
        if (isExplicitPathValid(filter, path, group.anchorShape)) return [{ path, group, isActive: true }];
        return getFilterShape(filter) === group.sourceShape ? [{ path, group, isActive: false }] : [];
      }),
    [explicitGroups, filter]
  );
  const pathsKey = useMemo(() => {
    const nextPaths = new Set(evaluatedExplicitGroups.filter(({ isActive }) => isActive).map(({ path }) => path));
    collectRequiredExpandedGroupPaths(filter, hasListTab, '', nextPaths);
    return [...nextPaths].sort().join(' ');
  }, [evaluatedExplicitGroups, filter, hasListTab]);
  const paths = useMemo(() => new Set(pathsKey ? pathsKey.split(' ') : []), [pathsKey]);

  useEffect(() => {
    if (evaluatedExplicitGroups.length !== explicitGroups.size) {
      setExplicitGroups(new Map(evaluatedExplicitGroups.map(({ path, group }) => [path, group])));
    }
  }, [evaluatedExplicitGroups, explicitGroups.size]);

  return { expandGroup, paths };
};
